import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControlLabel, Grid, MenuItem, TextField, Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import moment from "moment-timezone";
import BookingFormContent from "@components/booking/BookingFormContent";
import {
  useCreateBooking, useUpdateBooking, useCreateBookingItem, useUpdateBookingItem,
  useMergeBookings, useDeleteBooking,
} from "@store/actions/bookingActions";
import { useCreateLead } from "@store/actions/leadActions";
import { GET_BOOKING } from "@store/operations/bookingOperations";
import { GET_ALL_LEADS } from "@store/operations/leadOpertations";

const TITLES = {
  "new-booking": "New booking", "edit-booking": "Edit booking", "new-lead": "New lead",
  "add-boat": "Add boat",
  crew: "Manage crew", merge: "Merge bookings", "delete-booking": "Delete booking", phone: "Phone number",
};

function leadLabel(lead) {
  const name = [lead.firstName, lead.lastName].filter(Boolean).join(" ");
  return [name, lead.email].filter(Boolean).join(" · ") || "Name unavailable";
}

const filterLeads = createFilterOptions({ limit: 100, stringify: leadLabel });

function sameValue(left, right) {
  return (left == null ? null : left) === (right == null ? null : right);
}

function bookingInput(booking, original) {
  const input = {};
  for (const field of ["groupSize", "hours", "catering", "event", "eventOther", "available", "notes"]) {
    let value = booking[field];
    if (["groupSize", "hours"].includes(field) && value !== "" && value != null) {
      value = Number(value);
      if (!Number.isInteger(value) || value < 0) throw new Error("Enter whole numbers for guests and hours.");
    }
    if (value === "" && ["groupSize", "hours"].includes(field)) value = null;
    if (original && ["groupSize", "hours"].includes(field) && value == null && original[field] != null) {
      throw new Error("Enter whole numbers for guests and hours.");
    }
    if (value !== undefined && (!original || !sameValue(value, original[field]))) input[field] = value;
  }
  if (!booking.lead?.id) throw new Error("Choose a lead for this booking.");
  if (!original || booking.lead.id !== original.lead?.id) input.lead = { id: booking.lead.id };
  if (booking.cruiseDate) {
    const cruiseDate = moment(booking.cruiseDate);
    if (!cruiseDate.isValid()) throw new Error("Choose a valid cruise date and time.");
    if (!original || cruiseDate.valueOf() !== moment(original.cruiseDate).valueOf()) input.cruiseDate = cruiseDate.toISOString();
  } else if (original?.cruiseDate) {
    throw new Error("Choose a valid cruise date and time.");
  }
  if (!original) input.source = "Manual";
  return input;
}

export default function BookingDashboardNativeDialog({ request, bookings, catalog, onClose, onSaved, onChanged }) {
  const action = request.action;
  const needsBooking = Boolean(request.bookingId) && action !== "phone";
  const needsLeads = ["new-booking", "edit-booking", "new-lead"].includes(action);
  const bookingQuery = useQuery(GET_BOOKING, {
    variables: { id: request.bookingId }, skip: !needsBooking, fetchPolicy: "network-only",
  });
  const leadsQuery = useQuery(GET_ALL_LEADS, { skip: !needsLeads });
  const original = bookingQuery.data?.booking || null;
  const [booking, setBooking] = useState(action === "new-booking" ? { lead: request.leadId ? { id: request.leadId } : null } : null);
  const [itemInput, setItemInput] = useState(request.boatId ? { boat: { id: request.boatId } } : {});
  const [lead, setLead] = useState({});
  const [createdLead, setCreatedLead] = useState(null);
  const [creatingLead, setCreatingLead] = useState(false);
  const [boatId, setBoatId] = useState(request.boatId || "");
  const [itemId, setItemId] = useState(request.itemId || "");
  const [skippers, setSkippers] = useState([]);
  const [deckies, setDeckies] = useState([]);
  const [crewDirty, setCrewDirty] = useState(false);
  const [nestedCrew, setNestedCrew] = useState(null);
  const [destination, setDestination] = useState(request.bookingId || "");
  const [sources, setSources] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [discard, setDiscard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const savingRef = useRef(false);
  const initialBooking = useRef(null);
  const initialCrewItem = useRef(null);
  const staffList = catalog?.staffList || [];
  const boats = catalog?.boats || [];
  const leadOptions = createdLead && !leadsQuery.data?.leads?.some((entry) => entry.id === createdLead.id)
    ? [...(leadsQuery.data?.leads || []), createdLead] : leadsQuery.data?.leads || [];
  const [createBooking] = useCreateBooking();
  const [updateBooking] = useUpdateBooking();
  const [createLead] = useCreateLead();
  const [createItem] = useCreateBookingItem(original);
  const [updateItem] = useUpdateBookingItem(original);
  const [mergeBookings] = useMergeBookings();
  const [deleteBooking] = useDeleteBooking();

  useEffect(() => {
    if (original && !initialBooking.current) {
      initialBooking.current = original;
      setBooking(original);
      const availableItems = original.items || [];
      if (!request.itemId && availableItems.length === 1) setItemId(availableItems[0].id);
    }
  }, [original, request.itemId]);
  useEffect(() => {
    const item = original?.items?.find((entry) => entry.id === itemId);
    if (!item || initialCrewItem.current === itemId) return;
    initialCrewItem.current = itemId;
    setSkippers((item?.skippers || []).map((entry) => entry.staff).filter(Boolean));
    setDeckies((item?.deckies || []).map((entry) => entry.staff).filter(Boolean));
  }, [original, itemId]);

  const loading = (needsBooking && (bookingQuery.loading || !booking)) || (needsLeads && leadsQuery.loading);
  const readError = bookingQuery.error || leadsQuery.error
    || (needsBooking && !bookingQuery.loading && bookingQuery.data && !original)
    || (needsLeads && leadsQuery.data && !Array.isArray(leadsQuery.data.leads));
  const sameLeadBookings = original?.lead?.id
    ? bookings.filter((entry) => entry.lead?.id === original.lead.id) : [];
  const possibleSources = sameLeadBookings.filter((entry) => entry.id !== destination);
  const changeBooking = (value) => {
    setDirty(true);
    setBooking((current) => typeof value === "function" ? value(current) : value);
  };
  const changeItemInput = (value) => {
    setDirty(true);
    setItemInput((current) => typeof value === "function" ? value(current) : value);
  };
  const close = () => {
    if (savingRef.current) return;
    if (dirty) setDiscard(true); else onClose();
  };

  const save = async (event) => {
    event?.preventDefault();
    if (savingRef.current || loading || readError) return;
    setError("");
    let perform;
    try {
      if (action === "new-lead" || creatingLead) {
        const input = { firstName: lead.firstName?.trim(), lastName: lead.lastName?.trim(), email: lead.email?.trim(), phone: lead.phone?.trim() };
        if (!input.firstName || !input.lastName || !input.email) throw new Error("Enter the lead name and email.");
        perform = async () => {
          const response = await createLead({ variables: { input } });
          const saved = response.data?.createLead;
          if (!saved?.id) throw new Error("The lead could not be saved.");
          setCreatedLead(saved);
          if (creatingLead) {
            setBooking((current) => ({ ...current, lead: saved }));
            setLead({}); setCreatingLead(false); setDirty(true);
            try { await leadsQuery.refetch(); }
            catch (refreshError) { setError("The lead was saved, but the latest lead list could not be loaded. Please try again."); }
            if (onChanged) Promise.resolve(onChanged()).then((refreshed) => {
              if (!refreshed) setError("The lead was saved, but the latest details could not be loaded.");
            }).catch(() => setError("The lead was saved, but the latest details could not be loaded."));
            return null;
          }
          let refreshed = true;
          try { await leadsQuery.refetch(); } catch (refreshError) { refreshed = false; }
          return { id: saved.id, leadId: saved.id, refreshRequired: !refreshed };
        };
      } else if (["new-booking", "edit-booking"].includes(action)) {
        const input = bookingInput(booking, action === "edit-booking" ? initialBooking.current : null);
        if (!leadOptions.some((entry) => entry.id === booking.lead.id)) throw new Error("Choose a saved lead for this booking.");
        perform = async () => {
          const response = action === "edit-booking"
            ? await updateBooking({ variables: { id: request.bookingId, input } })
            : await createBooking({ variables: { input, itemInput: itemInput.boat?.id ? itemInput : undefined } });
          const saved = response.data?.[action === "edit-booking" ? "updateBooking" : "createBooking"];
          if (!saved?.id) throw new Error("The booking could not be saved.");
          return { id: saved.id, bookingId: saved.id };
        };
      } else if (action === "add-boat") {
        if (!boats.some((entry) => entry.id === boatId)) throw new Error("Choose a boat.");
        if (original.items?.some((entry) => entry.boat?.id === boatId)) throw new Error("This boat is already part of the booking.");
        perform = async () => {
          const response = await createItem({ variables: { bookingID: request.bookingId, input: { boat: { id: boatId } } } });
          const id = response.data?.createBookingItem?.id;
          if (!id) throw new Error("The boat could not be added.");
          return { id, bookingId: request.bookingId };
        };
      } else if (action === "crew") {
        const item = original.items?.find((entry) => entry.id === itemId);
        if (!item) throw new Error("Choose a boat item.");
        const crew = [...skippers, ...deckies];
        if (crew.some((entry) => !staffList.some((staff) => staff.id === entry.id))) throw new Error("Choose saved crew members.");
        perform = async () => {
          const response = await updateItem({ variables: { id: itemId, input: {
            baseCost: item.baseCost, commision: item.commision,
            skippers: skippers.map((entry) => ({ id: entry.id })), deckies: deckies.map((entry) => ({ id: entry.id })),
          } } });
          const id = response.data?.updateBookingItem?.id;
          if (!id) throw new Error("Crew assignments could not be saved.");
          return { id, bookingId: request.bookingId };
        };
      } else if (action === "merge") {
        if (!sameLeadBookings.some((entry) => entry.id === destination) || !sources.length
          || sources.some((id) => id === destination || !possibleSources.some((entry) => entry.id === id))) {
          throw new Error("Choose a destination and at least one source booking for this lead.");
        }
        perform = async () => {
          const response = await mergeBookings({ variables: { from: sources, to: destination } });
          const id = response.data?.mergeBookings?.id;
          if (id !== destination) throw new Error("These bookings could not be merged.");
          return { id, bookingId: id };
        };
      } else if (action === "delete-booking") {
        perform = async () => {
          const response = await deleteBooking({ variables: { id: request.bookingId } });
          const id = response.data?.deleteBooking?.id;
          if (!id) throw new Error("The booking could not be deleted.");
          return { id, bookingId: id };
        };
      }
    } catch (validationError) {
      setError(validationError.message);
      return;
    }
    if (!perform) return;
    savingRef.current = true; setSaving(true);
    let result;
    try {
      result = await perform();
    } catch (saveError) {
      setError("This could not be saved. Your changes are still here. Please try again.");
    } finally {
      savingRef.current = false; setSaving(false);
    }
    if (result) onSaved(result);
  };

  const leadFields = (
    <Grid container spacing={2}>
      {[["firstName", "First name"], ["lastName", "Last name"], ["email", "Email"], ["phone", "Phone"]].map(([field, label]) => (
        <Grid item xs={12} sm={6} key={field}>
          <TextValidator fullWidth name={field} label={label} value={lead[field] || ""}
            validators={field === "phone" ? [] : field === "email" ? ["required", "isEmail"] : ["required"]}
            errorMessages={field === "phone" ? [] : field === "email" ? ["Email is required", "Enter a valid email"] : [`${label} is required`]}
            onChange={(event) => { setDirty(true); setLead({ ...lead, [field]: event.target.value }); }} />
        </Grid>
      ))}
    </Grid>
  );
  const staffName = (staff) => [staff.firstName, staff.lastName].filter(Boolean).join(" ") || staff.email || "Name unavailable";

  return (
    <>
      <Dialog open fullWidth maxWidth={["new-booking", "edit-booking", "merge"].includes(action) && !creatingLead ? "md" : "sm"}
        onClose={close} disableBackdropClick disableEscapeKeyDown={saving}>
        <ValidatorForm onSubmit={save}>
          <DialogTitle>{creatingLead ? "New lead" : TITLES[action]}</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error">{error}</Alert>}
            {readError ? <Alert severity="error">Details could not be loaded. <Button onClick={() => { if (needsBooking) bookingQuery.refetch(); if (needsLeads) leadsQuery.refetch(); }}>Try again</Button></Alert>
              : loading ? <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                : <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: 0, pointerEvents: saving ? "none" : undefined }}>
                  {action === "new-lead" || creatingLead ? leadFields : null}
                  {action === "phone" && <>
                    <Typography variant="body1" style={{ userSelect: "text" }}>{request.phone}</Typography>
                    <Box mt={2}><Button onClick={async () => {
                      try { await navigator.clipboard.writeText(request.phone); }
                      catch (copyError) { setError("The number could not be copied. Select it and copy it manually."); }
                    }}>Copy number</Button></Box>
                  </>}
                  {action === "delete-booking" && <>
                    <DialogContentText>Delete booking {original.reference}?</DialogContentText>
                    <Typography>This booking will be removed from the booking list.</Typography>
                  </>}
                  {["new-booking", "edit-booking"].includes(action) && !creatingLead && booking && (
                    <>
                      <Box display="flex" justifyContent="flex-end" mb={2}><Button onClick={() => { setError(""); setCreatingLead(true); }}>New lead</Button></Box>
                      <Autocomplete options={leadOptions} filterOptions={filterLeads} getOptionLabel={leadLabel}
                        value={leadOptions.find((entry) => entry.id === booking.lead?.id) || null}
                        getOptionSelected={(option, selected) => option.id === selected.id}
                        onChange={(_, selected) => changeBooking({ ...booking, lead: selected })}
                        noOptionsText="No matching leads"
                        renderInput={(params) => <TextField {...params} required label="Lead" fullWidth variant="outlined" margin="normal"
                          placeholder="Search by name or email" helperText="Search by name or email to choose a lead." />} />
                      <BookingFormContent booking={booking} setBooking={changeBooking} isNew={action === "new-booking"}
                        itemInput={itemInput} setItemInput={changeItemInput} showLead={false}
                        onEditItem={(item) => setNestedCrew({ action: "crew", bookingId: request.bookingId, itemId: item.id })} />
                      <TextField label="Notes" fullWidth multiline minRows={3} variant="outlined" margin="normal" value={booking.notes || ""}
                        onChange={(event) => changeBooking({ ...booking, notes: event.target.value })} />
                    </>
                  )}
                  {action === "add-boat" && <TextField select label="Boat" fullWidth variant="outlined" value={boatId} onChange={(event) => { setBoatId(event.target.value); setDirty(true); }}>
                    {boats.filter((boat) => !original.items?.some((item) => item.boat?.id === boat.id)).map((boat) => <MenuItem key={boat.id} value={boat.id}>{boat.name}</MenuItem>)}
                  </TextField>}
                  {action === "crew" && <Grid container spacing={2}>
                    <Grid item xs={12}><TextField select fullWidth label="Boat item" variant="outlined" value={itemId} onChange={(event) => {
                      if (crewDirty) { setError("Save your crew changes before choosing another boat."); return; }
                      setItemId(event.target.value);
                    }}>
                      {(original.items || []).map((item) => <MenuItem key={item.id} value={item.id}>{item.boat?.name || "Boat unavailable"}</MenuItem>)}
                    </TextField></Grid>
                    {[["Skippers", skippers, setSkippers], ["Deckhands", deckies, setDeckies]].map(([label, value, setValue]) => <Grid item xs={12} key={label}>
                      <Autocomplete multiple options={staffList} value={value} getOptionLabel={staffName}
                        getOptionSelected={(option, selected) => option.id === selected.id}
                        onChange={(_, selected) => { setValue(selected); setDirty(true); setCrewDirty(true); }}
                        renderInput={(params) => <TextField {...params} label={label} variant="outlined" />} />
                    </Grid>)}
                    {!original.items?.length && <Grid item xs={12}><Typography>No boat items have been added to this booking.</Typography></Grid>}
                  </Grid>}
                  {action === "merge" && <>
                    <DialogContentText>Choose the booking to keep and the source bookings to merge into it.</DialogContentText>
                    <TextField select label="Destination booking" fullWidth variant="outlined" value={destination} onChange={(event) => {
                      setDestination(event.target.value); setSources((current) => current.filter((id) => id !== event.target.value)); setDirty(true);
                    }}>{sameLeadBookings.map((entry) => <MenuItem key={entry.id} value={entry.id}>{entry.reference}</MenuItem>)}</TextField>
                    {possibleSources.length ? <>
                      <FormControlLabel label="Select all source bookings" control={<Checkbox checked={sources.length === possibleSources.length}
                        onChange={(_, checked) => { setSources(checked ? possibleSources.map((entry) => entry.id) : []); setDirty(true); }} />} />
                      {possibleSources.map((entry) => <Box key={entry.id}><FormControlLabel label={`${entry.reference} · ${moment(entry.createdAt).format("ll")}`} control={<Checkbox checked={sources.includes(entry.id)}
                        onChange={(_, checked) => { setSources((current) => checked ? [...current, entry.id] : current.filter((id) => id !== entry.id)); setDirty(true); }} />} /></Box>)}
                    </> : <Typography>No other bookings for this lead are available to merge.</Typography>}
                  </>}
                </fieldset>}
          </DialogContent>
          <DialogActions>
            {creatingLead && <Button disabled={saving} onClick={() => { setCreatingLead(false); setError(""); }}>Back to booking</Button>}
            <Button disabled={saving} onClick={close}>Cancel</Button>
            {action === "phone" ? <Button component="a" href={request.phoneURI} color="primary">Call</Button> : <Button type="submit" color={action === "delete-booking" ? "secondary" : "primary"} disabled={saving || loading || Boolean(readError)
              || (action === "crew" && !itemId) || (action === "merge" && !possibleSources.length)}>
              {saving ? "Saving..." : action === "delete-booking" ? "Delete booking" : action === "merge" ? "Merge bookings" : "Save"}
            </Button>}
          </DialogActions>
        </ValidatorForm>
      </Dialog>
      <Dialog open={discard} onClose={() => setDiscard(false)}>
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent><DialogContentText>Your unsaved changes will be lost.</DialogContentText></DialogContent>
        <DialogActions><Button onClick={() => setDiscard(false)}>Keep editing</Button><Button color="secondary" onClick={onClose}>Discard changes</Button></DialogActions>
      </Dialog>
      {nestedCrew && <BookingDashboardNativeDialog request={nestedCrew} bookings={bookings} catalog={catalog}
        onClose={() => setNestedCrew(null)} onChanged={onChanged}
        onSaved={async () => {
          setNestedCrew(null);
          try {
            const response = await bookingQuery.refetch();
            setBooking((current) => ({ ...current, items: response.data.booking.items }));
            if (onChanged && !await onChanged()) setError("Crew assignments were saved, but the latest details could not be loaded.");
          } catch (refreshError) {
            setError("Crew assignments were saved, but the latest booking details could not be loaded.");
          }
        }} />}
    </>
  );
}
